/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { EuiSelectableProps } from '@elastic/eui'
import {
  EuiFilterButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopover,
  EuiSelectable,
  useGeneratedHtmlId,
} from '@elastic/eui'

import CloudProvider from '@modules/cloud-components/CloudProvider'
import { getRegionInfoById } from '@modules/cloud-lib/providers'
import { FormattedMessage } from '@modules/i18n'

type Props = {
  regionIds: string[]
}

const RegionFilter: React.FunctionComponent<Props> = ({ regionIds }) => {
  const htmlId = useGeneratedHtmlId()
  const [isOpen, setIsOpen] = useState(false)

  const [params, setParams] = useSearchParams()
  const selectedRegionIds = (params.get('region') || null)?.split(',') || []

  const options: EuiSelectableProps['options'] = regionIds.map((regionId) => ({
    label: regionId,
    checked: selectedRegionIds.includes(regionId) ? 'on' : undefined,
  }))

  const button = (
    <EuiFilterButton
      iconType='arrowDown'
      badgeColor='success'
      onClick={() => setIsOpen(!isOpen)}
      isSelected={isOpen}
      numFilters={regionIds.length}
      hasActiveFilters={Boolean(selectedRegionIds.length)}
      numActiveFilters={regionIds.length}
    >
      <FormattedMessage id='networkSecurity.regionFilter.label' defaultMessage='Region' />
    </EuiFilterButton>
  )

  const renderOption: EuiSelectableProps['renderOption'] = (option) => {
    const regionInfo = getRegionInfoById(option.label)

    return (
      <EuiFlexGroup gutterSize='m' alignItems='center'>
        <EuiFlexItem grow={false}>
          <CloudProvider id={regionInfo.provider.id} text='none' showIcon={true} />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>{regionInfo.region.cspRegionId}</EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  const onChange: EuiSelectableProps['onChange'] = (newOptions) => {
    const region = newOptions
      .filter((item) => item.checked === 'on')
      .map((item) => item.label)
      .join(',')

    setParams(
      (params) => {
        const newParams = new URLSearchParams(params)
        newParams.set('region', region)
        return newParams
      },
      {
        replace: true,
      },
    )
  }

  return (
    <EuiPopover
      id={htmlId}
      button={button}
      isOpen={isOpen}
      closePopover={() => setIsOpen(false)}
      panelPaddingSize='none'
    >
      <EuiSelectable
        aria-label='Region'
        options={options}
        renderOption={renderOption}
        onChange={onChange}
        listProps={{ isVirtualized: false }}
      >
        {(list) => <div style={{ width: '20rem' }}>{list}</div>}
      </EuiSelectable>
    </EuiPopover>
  )
}

export default RegionFilter
